import React from "react";

/**
 * HeroMosaic — original, brand-safe illustration for the homepage hero.
 *
 * Renders a fan-of-sticker-cards composition on a soccer-pitch background,
 * with three "cards" tilted in an overlapping stack. No Panini or FIFA
 * imagery, no player likenesses — the cards are stylized silhouettes with
 * shirt-and-ball motifs and are safe to ship in production. Designed to
 * replace stock photography that would otherwise misrepresent the sport
 * or infringe third-party trade dress.
 */
const P = {
  yellow: "#FFCB05",
  yellowSoft: "#FFE07A",
  red: "#E4002B",
  green: "#0B6E4F",
  greenDeep: "#053824",
  gold: "#C9A55A",
  ink: "#0A0A0A",
  paper: "#FFFDF6",
  cream: "#FFF7E1",
};

export default function HeroMosaic({ className = "" }) {
  return (
    <div className={`w-full aspect-[4/3] ${className}`}>
      <svg
        viewBox="0 0 800 600"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Illustration of collector sticker cards on a soccer-pitch background"
        className="w-full h-full"
      >
        {/* Pitch backdrop */}
        <defs>
          <linearGradient id="pitchGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={P.greenDeep} />
            <stop offset="60%" stopColor={P.green} />
            <stop offset="100%" stopColor={P.greenDeep} />
          </linearGradient>
          <radialGradient id="spot" cx="0.5" cy="0.35" r="0.7">
            <stop offset="0%" stopColor="rgba(255,203,5,0.28)" />
            <stop offset="70%" stopColor="rgba(255,203,5,0)" />
          </radialGradient>
          <pattern id="stripes" width="40" height="600" patternUnits="userSpaceOnUse">
            <rect width="40" height="600" fill="url(#pitchGrad)" />
            <rect x="0" width="20" height="600" fill="rgba(255,255,255,0.04)" />
          </pattern>
        </defs>

        <rect width="800" height="600" fill="url(#stripes)" />
        <rect width="800" height="600" fill="url(#spot)" />

        {/* Center-circle detail */}
        <circle cx="400" cy="300" r="150" fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="3" />
        <circle cx="400" cy="300" r="6" fill="rgba(255,255,255,0.25)" />

        {/* Speed lines */}
        <g stroke="rgba(255,255,255,0.10)" strokeWidth="2" fill="none">
          <path d="M0 150 L800 200" />
          <path d="M0 460 L800 500" />
        </g>

        {/* Card 1 — back left (green shirt) */}
        <g transform="translate(120 90) rotate(-12)">
          <rect x="0" y="0" width="220" height="320" rx="14" fill={P.paper} stroke={P.ink} strokeWidth="6" />
          <rect x="0" y="0" width="220" height="46" fill={P.yellow} />
          <rect x="0" y="46" width="220" height="6" fill={P.ink} />
          {/* Shirt silhouette */}
          <path
            d="M60 120 L40 140 L28 180 L54 194 L54 280 L166 280 L166 194 L192 180 L180 140 L160 120 L145 130 Q110 148 75 130 Z"
            fill={P.green}
            stroke={P.ink}
            strokeWidth="4"
          />
          <rect x="54" y="212" width="112" height="10" fill={P.ink} />
          <text x="110" y="264" textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontWeight="900" fontSize="30" fill={P.paper}>
            07
          </text>
          <rect x="16" y="292" width="80" height="18" rx="3" fill={P.red} />
          <rect x="102" y="292" width="102" height="18" rx="3" fill={P.ink} />
        </g>

        {/* Card 2 — center (red shirt with ball) */}
        <g transform="translate(300 60) rotate(2)">
          <rect x="0" y="0" width="240" height="360" rx="16" fill={P.paper} stroke={P.ink} strokeWidth="7" />
          <rect x="0" y="0" width="240" height="50" fill={P.red} />
          <rect x="0" y="50" width="240" height="6" fill={P.ink} />
          {/* Shirt */}
          <path
            d="M64 130 L44 152 L30 194 L58 208 L58 300 L182 300 L182 208 L210 194 L196 152 L176 130 L158 142 Q120 162 82 142 Z"
            fill={P.red}
            stroke={P.ink}
            strokeWidth="4"
          />
          <rect x="58" y="228" width="124" height="12" fill={P.ink} />
          <text x="120" y="286" textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontWeight="900" fontSize="34" fill={P.paper}>
            10
          </text>
          {/* Soccer ball emblem */}
          <circle cx="120" cy="180" r="24" fill={P.paper} stroke={P.ink} strokeWidth="3" />
          <polygon points="120,166 133,175 128,192 112,192 107,175" fill={P.ink} />
          {/* Foil corner */}
          <path d="M0 330 L240 330 L240 360 L0 360 Z" fill={P.gold} />
          <text x="120" y="352" textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontWeight="900" fontSize="14" fill={P.ink} letterSpacing="3">
            GOLD FOIL
          </text>
        </g>

        {/* Card 3 — right (yellow keeper shirt) */}
        <g transform="translate(500 100) rotate(11)">
          <rect x="0" y="0" width="210" height="310" rx="14" fill={P.paper} stroke={P.ink} strokeWidth="6" />
          <rect x="0" y="0" width="210" height="44" fill={P.ink} />
          <rect x="0" y="44" width="210" height="6" fill={P.yellow} />
          {/* Shirt */}
          <path
            d="M56 118 L38 138 L26 178 L52 192 L52 274 L158 274 L158 192 L184 178 L172 138 L154 118 L138 128 Q104 146 70 128 Z"
            fill={P.yellow}
            stroke={P.ink}
            strokeWidth="4"
          />
          <text x="105" y="248" textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontWeight="900" fontSize="28" fill={P.ink}>
            01
          </text>
          <rect x="16" y="284" width="94" height="16" rx="3" fill={P.yellow} stroke={P.ink} strokeWidth="1.5" />
          <rect x="118" y="284" width="76" height="16" rx="3" fill={P.ink} />
        </g>

        {/* Corner stickers / accents */}
        <g transform="translate(30 490) rotate(-6)">
          <rect x="0" y="0" width="130" height="46" rx="23" fill={P.paper} stroke={P.ink} strokeWidth="4" />
          <text x="65" y="30" textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontWeight="900" fontSize="16" fill={P.ink}>
            NUMBERED &amp; VERIFIED
          </text>
        </g>
        <g transform="translate(600 30) rotate(-4)">
          <rect x="0" y="0" width="150" height="48" rx="24" fill={P.yellow} stroke={P.ink} strokeWidth="4" />
          <text x="75" y="32" textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontWeight="900" fontSize="18" fill={P.ink}>
            MATCHDAY DROP
          </text>
        </g>
        <g transform="translate(640 460) rotate(8)">
          <circle cx="35" cy="35" r="34" fill={P.paper} stroke={P.ink} strokeWidth="4" />
          <polygon points="35,15 47,26 42,44 28,44 23,26" fill={P.ink} />
        </g>
      </svg>
    </div>
  );
}
